import { useEffect, useState } from "react";
import api, { qs, apiError } from "@/lib/api";
import { fmtINR } from "@/lib/service";
import { monthLabel } from "@/lib/dpc";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { ShieldCheck, TrendingUp, TrendingDown, Minus } from "lucide-react";

const TH = "px-2.5 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";
const lakh = (v) => (v >= 1e7 ? `${(v / 1e7).toFixed(1)} Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(1)} L` : fmtINR(v));
const shortMonth = (m) => monthLabel(m).replace(" 20", " '");

function ShareDelta({ latest }) {
  if (!latest || latest.delta_share == null) return null;
  const Icon = latest.delta_share > 0 ? TrendingUp : latest.delta_share < 0 ? TrendingDown : Minus;
  const tone = latest.delta_share > 0 ? "text-emerald-700" : latest.delta_share < 0 ? "text-red-600" : "text-muted-foreground";
  return <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${tone}`} data-testid="wty-trend-delta"><Icon size={12} />{latest.delta_share > 0 ? "+" : ""}{latest.delta_share} pts vs {shortMonth(latest.prev_month)}</span>;
}

function TrendTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs shadow-sm">
      <div className="font-semibold mb-1">{monthLabel(label)}</div>
      <div>GST billing <b className="num">₹{fmtINR(r.gst)}</b></div>
      <div>Warranty <b className="num">₹{fmtINR(r.warranty)}</b> <span className="text-muted-foreground">(labour ₹{fmtINR(r.warranty_labour)} · parts ₹{fmtINR(r.warranty_parts)})</span></div>
      <div className="text-muted-foreground">GS total ₹{fmtINR(r.total)} · {r.gs_ros} ROs · {r.claims} claims{r.share != null && <> · warranty share <b className="text-foreground">{r.share}%</b></>}</div>
    </div>
  );
}

export function WarrantyTrend({ month, workshop, testId = "svc-warranty-trend" }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/service/warranty-trend?${qs({ end: month, months: 12, workshop })}`).then(({ data }) => setD(data)).catch((e) => { toast.error(apiError(e), { id: "wty-trend" }); setD(false); });
  }, [month, workshop]);
  if (d === false) return null;
  if (!d) return <div className="border bg-card rounded-md p-4 h-64 animate-pulse" data-testid={testId} />;
  const t = d.totals;
  const noData = !d.rows.some((r) => r.has_data);
  return (
    <div className="border bg-card rounded-md" data-testid={testId}>
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><ShieldCheck size={14} /> Warranty vs GST billing — GS revenue by month</h3>
        <span className="text-[11px] text-muted-foreground">Kia-paid work as a share of GS revenue · {shortMonth(d.from)} → {shortMonth(d.to)} · {d.workshops} workshop{d.workshops === 1 ? "" : "s"}</span>
        {t.share != null && <span className="ml-auto text-xs"><span className="text-muted-foreground">period share</span> <b className="num" data-testid="wty-trend-share">{t.share}%</b> <span className="text-muted-foreground">· warranty ₹{lakh(t.warranty)} of ₹{lakh(t.total)}</span></span>}
        {d.latest && <span className="text-xs flex items-center gap-2"><span className="text-muted-foreground">{shortMonth(d.latest.month)}</span> <b className="num" data-testid="wty-trend-latest-share">{d.latest.share ?? 0}%</b><ShareDelta latest={d.latest} /></span>}
      </div>
      {noData ? <div className="px-4 py-10 text-center text-xs text-muted-foreground" data-testid="wty-trend-empty">No GS or warranty revenue in this window yet.</div> : (
        <>
          <div className="p-4 pb-2">
            <ResponsiveContainer width="100%" height={230}>
              <ComposedChart data={d.rows} margin={{ top: 8, right: 8, left: -4, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e5e9f0" />
                <XAxis dataKey="month" tickFormatter={shortMonth} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="rev" tick={{ fontSize: 10 }} tickFormatter={(v) => (v >= 1e5 ? `${Math.round(v / 1e5)}L` : `${Math.round(v / 1000)}k`)} />
                <YAxis yAxisId="pct" orientation="right" domain={[0, (max) => Math.max(10, Math.ceil(max / 10) * 10)]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip content={<TrendTip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="rev" dataKey="gst" name="GST billing" stackId="gs" fill="#0f172a" />
                <Bar yAxisId="rev" dataKey="warranty" name="Warranty (Kia-paid)" stackId="gs" fill="#059669" radius={[2, 2, 0, 0]} />
                <Line yAxisId="pct" type="monotone" dataKey="share" name="Warranty share %" stroke="#b45309" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-auto border-t">
            <table className="w-full text-xs" data-testid="wty-trend-table">
              <thead><tr className="border-b"><th className={`${TH} text-left`}>Month</th><th className={TH}>GST billing ₹</th><th className={TH}>Warranty ₹</th><th className={TH}>GS total ₹</th><th className={TH}>Share</th><th className={TH}>GS ROs</th><th className={TH}>Wty / RO</th><th className={TH}>Claims</th></tr></thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.month} className={`border-b last:border-0 ${r.has_data ? "" : "text-muted-foreground"}`} data-testid={`wty-trend-row-${r.month}`}>
                    <td className="px-2.5 py-1 font-medium whitespace-nowrap">{monthLabel(r.month)}</td>
                    <td className="px-2.5 py-1 num text-right">{r.has_data ? fmtINR(r.gst) : "—"}</td>
                    <td className="px-2.5 py-1 num text-right text-emerald-700 font-semibold">{r.has_data ? fmtINR(r.warranty) : "—"}</td>
                    <td className="px-2.5 py-1 num text-right">{r.has_data ? fmtINR(r.total) : "—"}</td>
                    <td className="px-2.5 py-1 num text-right font-bold">{r.share != null ? `${r.share}%` : "—"}</td>
                    <td className="px-2.5 py-1 num text-right">{r.has_data ? r.gs_ros : "—"}</td>
                    <td className="px-2.5 py-1 num text-right">{r.warranty_per_ro != null ? fmtINR(r.warranty_per_ro) : "—"}</td>
                    <td className="px-2.5 py-1 num text-right">{r.has_data ? r.claims : "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-secondary/40 font-semibold">
                <tr data-testid="wty-trend-total">
                  <td className="px-2.5 py-1">Period</td>
                  <td className="px-2.5 py-1 num text-right">{fmtINR(t.gst)}</td>
                  <td className="px-2.5 py-1 num text-right text-emerald-700">{fmtINR(t.warranty)}</td>
                  <td className="px-2.5 py-1 num text-right">{fmtINR(t.total)}</td>
                  <td className="px-2.5 py-1 num text-right">{t.share != null ? `${t.share}%` : "—"}</td>
                  <td className="px-2.5 py-1 num text-right">{d.rows.reduce((s, r) => s + r.gs_ros, 0)}</td>
                  <td />
                  <td className="px-2.5 py-1 num text-right">{t.claims}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="px-4 py-2 text-[10px] text-muted-foreground border-t">GST billing = GS labour + parts by GST invoice date · Warranty = Kia-approved labour (incl. sublet & other base) + parts by warranty invoice date · months before the first upload show no data</p>
        </>
      )}
    </div>
  );
}
